import { useEffect, useMemo, useState } from 'react';

const hasValue = (value) => {
  if (typeof value === 'string') {
    return value.trim() !== '' && value !== '__none__';
  }
  return value !== undefined && value !== null;
};

export const useCustomizationFlow = ({
  categories = [],
  values = {},
  isOptionVisible,
  requireMandatory = true,
}) => {
  const visibleSections = useMemo(() => {
    return (categories || []).map((category) => ({
      ...category,
      visibleOptions: (category.options || []).filter((option) => {
        if (typeof isOptionVisible !== 'function') return true;
        return isOptionVisible(option, values);
      }),
    }));
  }, [categories, values, isOptionVisible]);

  const [currentSectionId, setCurrentSectionId] = useState(visibleSections[0]?.category_id || null);

  useEffect(() => {
    if (!visibleSections.length) {
      setCurrentSectionId(null);
      return;
    }

    const currentExists = visibleSections.some((section) => section.category_id === currentSectionId);
    if (!currentExists) {
      setCurrentSectionId(visibleSections[0]?.category_id || null);
    }
  }, [visibleSections, currentSectionId]);

  const sectionIndex = visibleSections.findIndex((section) => section.category_id === currentSectionId);
  const currentSection = sectionIndex >= 0 ? visibleSections[sectionIndex] : visibleSections[0] || null;

  const missingBySection = useMemo(() => {
    const map = {};

    visibleSections.forEach((section) => {
      const missing = (section.visibleOptions || []).filter((option) => {
        if (!option.is_required || !requireMandatory) return false;
        return !hasValue(values[option.option_id]);
      });
      map[section.category_id] = missing;
    });

    return map;
  }, [visibleSections, values, requireMandatory]);

  const allMissingRequired = useMemo(() => {
    return visibleSections.flatMap((section) => (missingBySection[section.category_id] || []).map((option) => ({
      section,
      option,
    })));
  }, [visibleSections, missingBySection]);

  const currentMissingRequired = currentSection ? (missingBySection[currentSection.category_id] || []) : [];

  const hasPrevious = sectionIndex > 0;
  const hasNext = sectionIndex >= 0 && sectionIndex < visibleSections.length - 1;

  const goPrevious = () => {
    if (!hasPrevious) return;
    setCurrentSectionId(visibleSections[sectionIndex - 1]?.category_id || null);
  };

  const goNext = () => {
    if (!hasNext) return;
    setCurrentSectionId(visibleSections[sectionIndex + 1]?.category_id || null);
  };

  const canGoNext = !requireMandatory || currentMissingRequired.length === 0;
  const canSubmit = !requireMandatory || allMissingRequired.length === 0;

  return {
    visibleSections,
    currentSectionId,
    currentSection,
    setCurrentSectionId,
    missingBySection,
    currentMissingRequired,
    allMissingRequired,
    hasPrevious,
    hasNext,
    goPrevious,
    goNext,
    canGoNext,
    canSubmit,
  };
};
